import { type ReactNode } from 'react';
import { EmploymentType } from '@brighte/validation';
import { TextLink } from '@spark-web/text-link';

export enum IncomeVerificationErrorCode {
  PAYSLIP_INSUFFICIENT_COUNT = 'PAYSLIP_INSUFFICIENT_COUNT',
  PAYSLIP_VERIFICATION_FAILED = 'PAYSLIP_VERIFICATION_FAILED',
  BANK_STATEMENT_INSUFFICIENT_COVERAGE = 'BANK_STATEMENT_INSUFFICIENT_COVERAGE',
  BANK_STATEMENT_OUT_OF_DATE = 'BANK_STATEMENT_OUT_OF_DATE',
  BANK_STATEMENT_UNREADABLE = 'BANK_STATEMENT_UNREADABLE',
  NOTICE_OF_ASSESSMENT_OUT_OF_DATE = 'NOTICE_OF_ASSESSMENT_OUT_OF_DATE',
  INCOME_TAX_RETURN_OUT_OF_DATE = 'INCOME_TAX_RETURN_OUT_OF_DATE',
  DOCUMENT_TYPE_MISMATCH = 'DOCUMENT_TYPE_MISMATCH',
  DOCUMENT_UNREADABLE = 'DOCUMENT_UNREADABLE',
  MULTIPLE_DOCUMENT_TYPE_FAILED = 'MULTIPLE_DOCUMENT_TYPE_FAILED',
  // Emitted by the backend but previously absent here, so they fell through to the
  // DOCUMENT_UNREADABLE default and told the customer their file was unreadable when it wasn't.
  DOCUMENT_PROCESSING_FAILED = 'DOCUMENT_PROCESSING_FAILED',
  DOCUMENT_DOES_NOT_MATCH_REQUEST = 'DOCUMENT_DOES_NOT_MATCH_REQUEST',
  NOTICE_OF_ASSESSMENT_ZERO_INCOME = 'NOTICE_OF_ASSESSMENT_ZERO_INCOME',

  PAYSLIP_NO_DATE = 'PAYSLIP_NO_DATE',
  PAYSLIP_INVALID_DATE = 'PAYSLIP_INVALID_DATE',
  PAYSLIP_DATE_OUT_OF_RANGE = 'PAYSLIP_DATE_OUT_OF_RANGE',
  BANK_STATEMENT_NO_DATE_RANGE = 'BANK_STATEMENT_NO_DATE_RANGE',
  BANK_STATEMENT_INVALID_FORMAT = 'BANK_STATEMENT_INVALID_FORMAT',
  BANK_STATEMENT_INVALID_DATES = 'BANK_STATEMENT_INVALID_DATES',
  BANK_STATEMENT_END_DATE_OUT_OF_RANGE = 'BANK_STATEMENT_END_DATE_OUT_OF_RANGE',
  UNKNOWN_DOCUMENT_TYPE = 'UNKNOWN_DOCUMENT_TYPE',
  TAX_ASSESSMENT_NO_YEAR = 'TAX_ASSESSMENT_NO_YEAR',
  NOTICE_OF_ASSESSMENT_NO_YEAR = 'NOTICE_OF_ASSESSMENT_NO_YEAR',
  TAX_ASSESSMENT_OUT_OF_DATE = 'TAX_ASSESSMENT_OUT_OF_DATE',
}

export type IncomeVerificationErrorContent = {
  title: string;
  description?: string;
  callout?: ReactNode;
  guidanceTitle?: string;
  guidanceItems?: string[];
  /**
   * Append the sentence naming what this applicant should upload instead. Set where the failure
   * means "this is the wrong document", so the customer is told the cause and the fix together.
   */
  requiresDocumentPrompt?: boolean;
};

/**
 * What to upload, per declared income type, as one sentence that can follow a cause sentence.
 * Deliberately narrower than UPLOAD_PRIMARY_INCOME_DOCUMENTS_DESCRIPTION_BY_EMPLOYMENT_TYPE (which
 * renders the full bulleted list on the step): after a failure the customer needs one clear
 * instruction, not the whole menu.
 */
const REQUIRED_DOCUMENTS_SENTENCE: Record<EmploymentType, string> = {
  [EmploymentType.EMPLOYED]:
    'Please upload two payslips from the last 3 months, or a personal Notice of Assessment from the last 18 months.',
  [EmploymentType.SELF_EMPLOYED]:
    'Please upload your most recent Notice of Assessment or individual tax return from the last 18 months.',
  [EmploymentType.PENSIONER]:
    'Please upload a current pension, Centrelink or superannuation statement, or a bank statement covering the last 90 days.',
  [EmploymentType.OTHER]:
    'Please upload a bank statement covering the last 90 days, or a Notice of Assessment from the last 18 months.',
};

// Names no section: the "What documents can I upload?" accordion this used to point at is gone,
// and the accepted documents are now listed on the page itself for every income type.
const REQUIRED_DOCUMENTS_FALLBACK = 'Please upload one of the accepted income documents listed on this page.';

/** The sentence naming what this applicant should upload, for an unset or unmapped income type. */
export const getRequiredDocumentsSentence = (employmentType?: EmploymentType): string =>
  (employmentType && REQUIRED_DOCUMENTS_SENTENCE[employmentType]) ?? REQUIRED_DOCUMENTS_FALLBACK;

const ATO_LINK = (
  <TextLink href="https://ato.gov.au" target="_blank" rel="noopener noreferrer">
    ato.gov.au
  </TextLink>
);

const PAYSLIP_VERIFICATION_FAILED_CONTENT: IncomeVerificationErrorContent = {
  title: "We couldn't verify your payslips",
  description:
    'The payslips you uploaded are either too old or could not be read as payslips. We need 2 recent payslips to verify your income.',
};

// The three payslip date failures used to share PAYSLIP_VERIFICATION_FAILED_CONTENT, which blurred
// "we couldn't find a date" and "the date is too old" into one message — different fixes, so they
// are now separate.
const PAYSLIP_NO_DATE_CONTENT: IncomeVerificationErrorContent = {
  title: 'We couldn’t find a pay date',
  description:
    'We couldn’t find the pay date on your payslip. Upload the original payslip from your payroll system, where the pay date is printed, rather than a photo or screenshot.',
};

const PAYSLIP_INVALID_DATE_CONTENT: IncomeVerificationErrorContent = {
  title: 'We couldn’t read the pay date',
  description:
    'We found a pay date on your payslip but couldn’t read it. Upload the original payslip from your payroll system, not a cropped or edited copy.',
};

const PAYSLIP_DATE_OUT_OF_RANGE_CONTENT: IncomeVerificationErrorContent = {
  title: 'Your payslip is too old',
  description: 'The payslip you uploaded is older than 3 months. We need 2 payslips from the last 3 months.',
};

const BANK_STATEMENT_INSUFFICIENT_COVERAGE_CONTENT: IncomeVerificationErrorContent = {
  title: "Your bank statement doesn't cover enough time",
  description:
    'Your bank statement does not cover enough days. We need at least 90 days (3 months) of transaction history to verify your income.',
  callout:
    "Most bank apps let you export a custom date range. Export from 3 months ago to today's date to make sure it covers enough time.",
};

const BANK_STATEMENT_OUT_OF_DATE_CONTENT: IncomeVerificationErrorContent = {
  title: 'Your bank statement is out of date',
  description:
    'Your bank statement ends too far in the past. The most recent transaction on the statement needs to be from within the last month.',
};

const INCOME_TAX_RETURN_OUT_OF_DATE_CONTENT: IncomeVerificationErrorContent = {
  title: 'Your income tax return is out of date',
  description:
    "The income tax return you uploaded isn't from the most recent financial year. We need a return from within the last 18 months.",
  callout: (
    <>You can access a copy of your tax return through your accountant or download it from myGov at {ATO_LINK}.</>
  ),
};

const NOTICE_OF_ASSESSMENT_OUT_OF_DATE_CONTENT: IncomeVerificationErrorContent = {
  title: 'Your Notice of Assessment is out of date',
  description:
    "The Notice of Assessment you uploaded isn't from the most recent financial year. We need a document from within the last 18 months.",
  callout: <>You can download your Notice of Assessment from myGov at {ATO_LINK}.</>,
};

const DOCUMENT_TYPE_MISMATCH_CONTENT: IncomeVerificationErrorContent = {
  title: 'We couldn’t verify your income documents',
  description: 'The document you uploaded was the wrong document type.',
  // No accepted-documents list here: requiresDocumentPrompt already names what to upload, and the
  // full list is on the step itself.
  requiresDocumentPrompt: true,
};

export const INCOME_VERIFICATION_ERROR_CONTENT: Record<IncomeVerificationErrorCode, IncomeVerificationErrorContent> = {
  [IncomeVerificationErrorCode.PAYSLIP_INSUFFICIENT_COUNT]: {
    title: 'We need one more payslip',
    description: 'We found 1 payslip from the last 3 months. We need 2 to verify your income.',
  },
  [IncomeVerificationErrorCode.PAYSLIP_VERIFICATION_FAILED]: PAYSLIP_VERIFICATION_FAILED_CONTENT,
  [IncomeVerificationErrorCode.PAYSLIP_NO_DATE]: PAYSLIP_NO_DATE_CONTENT,
  [IncomeVerificationErrorCode.PAYSLIP_INVALID_DATE]: PAYSLIP_INVALID_DATE_CONTENT,
  [IncomeVerificationErrorCode.PAYSLIP_DATE_OUT_OF_RANGE]: PAYSLIP_DATE_OUT_OF_RANGE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_INSUFFICIENT_COVERAGE]: BANK_STATEMENT_INSUFFICIENT_COVERAGE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_NO_DATE_RANGE]: BANK_STATEMENT_INSUFFICIENT_COVERAGE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_OUT_OF_DATE]: BANK_STATEMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_INVALID_FORMAT]: BANK_STATEMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_INVALID_DATES]: BANK_STATEMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_END_DATE_OUT_OF_RANGE]: BANK_STATEMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_UNREADABLE]: {
    title: 'We couldn’t read your bank statement',
    description:
      'We were unable to extract date information from your bank statement. This can happen if the file is in an unusual format, or the key details aren’t visible.',
  },
  [IncomeVerificationErrorCode.NOTICE_OF_ASSESSMENT_OUT_OF_DATE]: NOTICE_OF_ASSESSMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.NOTICE_OF_ASSESSMENT_NO_YEAR]: NOTICE_OF_ASSESSMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.INCOME_TAX_RETURN_OUT_OF_DATE]: INCOME_TAX_RETURN_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.TAX_ASSESSMENT_NO_YEAR]: INCOME_TAX_RETURN_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.TAX_ASSESSMENT_OUT_OF_DATE]: INCOME_TAX_RETURN_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.DOCUMENT_TYPE_MISMATCH]: DOCUMENT_TYPE_MISMATCH_CONTENT,
  [IncomeVerificationErrorCode.UNKNOWN_DOCUMENT_TYPE]: DOCUMENT_TYPE_MISMATCH_CONTENT,
  [IncomeVerificationErrorCode.DOCUMENT_UNREADABLE]: {
    title: 'We couldn’t verify your income documents',
    description:
      "We weren't able to read the file you uploaded. This can happen if it's blurry, corrupted, password-protected, or in a format we don't support.",
  },
  [IncomeVerificationErrorCode.MULTIPLE_DOCUMENT_TYPE_FAILED]: {
    title: 'We couldn’t verify your documents',
  },
  // Our pipeline failed, not the customer's file — so this must not say the document was unreadable.
  [IncomeVerificationErrorCode.DOCUMENT_PROCESSING_FAILED]: {
    title: 'We couldn’t check your document',
    description:
      'Something went wrong on our side while checking your document — there’s nothing wrong with the file you uploaded. Please try uploading it again.',
  },
  [IncomeVerificationErrorCode.DOCUMENT_DOES_NOT_MATCH_REQUEST]: {
    title: 'That’s not the document we asked for',
    description: 'The document you uploaded isn’t the one we requested.',
    requiresDocumentPrompt: true,
  },
  [IncomeVerificationErrorCode.NOTICE_OF_ASSESSMENT_ZERO_INCOME]: {
    title: 'Your Notice of Assessment shows no income',
    description:
      'We could read your Notice of Assessment, but it shows $0 taxable income, so we can’t use it to verify your income. If you receive a pension or another income, change your income type above, or upload a document showing your current income.',
  },
};
